import { z } from "zod";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { type FC, useCallback, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useRenameStyle } from "../hooks/style.js";

interface RenameMapModalProps {
  opened: boolean;
  onClose: () => void;
  currentName: string;
  styleId: number;
}

export const RenameMapModal: FC<RenameMapModalProps> = ({ opened, onClose, currentName, styleId }) => {
  const intl = useIntl();
  const renameStyle = useRenameStyle(styleId);

  const validate = zod4Resolver(
    z.object({
      displayName: z
        .string()
        .min(1, {
          message: intl.formatMessage({
            defaultMessage: "Name is required",
            id: "error.nameRequired",
          }),
        })
        .max(255, {
          message: intl.formatMessage({
            defaultMessage: "Name is too long",
            id: "error.nameTooLong",
          }),
        }),
    }),
  );

  const { onSubmit, getInputProps, setFieldValue } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      displayName: currentName,
    },
    validate,
  });

  useEffect(() => {
    setFieldValue("displayName", currentName);
  }, [currentName]);

  const handleRename = useCallback(
    ({ displayName }: { displayName: string }) => {
      const trimmed = displayName.trim();
      if (trimmed !== currentName) {
        renameStyle(trimmed);
      }
      onClose();
    },
    [currentName, renameStyle, onClose],
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onClick={(e) => {
        e.stopPropagation();
      }}
      title={intl.formatMessage({ defaultMessage: "Rename map", id: "title.renameMap" })}
    >
      <form onSubmit={onSubmit(handleRename)}>
        <Stack gap="md">
          <TextInput
            label={intl.formatMessage({
              defaultMessage: "Name",
              id: "label.name",
            })}
            required
            {...getInputProps("displayName")}
          />

          <Group gap="md" justify="flex-end" mt="md">
            <Button variant="secondary" onClick={onClose}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="tertiary" type="submit">
              <FormattedMessage id="button.rename" defaultMessage="Rename" />
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
